import Feature from "ol/Feature";
import MapBrowserEvent from "ol/MapBrowserEvent.js";
import Draw, { DrawEvent } from "ol/interaction/Draw.js";

// @ts-ignore
import { lineStyle, pointStyle, polygonStyle } from "./TiffStyles.ts";
// @ts-ignore
import LineDistanceMeasure2D from "../../../features/measures/lineDistance2D.js";
// @ts-ignore
import PolygonArea2D from "../../../features/measures/polygonArea2D.js";
// @ts-ignore
import Point2D from "../../../features/measures/point2D.js";
import {
  Measure,
  SerializedMeasure,
} from "../../../utils/types/TiffViewerTypes.js";
import { MutableRefObject } from "react";

export enum ToolMode {
  NONE = "none",
  DISTANCE = "distance",
  AREA = "area",
  POINT = "point",
}

export type DrawType = "LineString" | "Polygon" | "Point";

export const getDrawType = (toolMode: ToolMode) => {
  switch (toolMode) {
    case ToolMode.DISTANCE:
      return "LineString";
    case ToolMode.AREA:
      return "Polygon";
    case ToolMode.POINT:
      return "Point";
    default:
      return null;
  }
};

export const getStyleForDrawType = (drawType?: DrawType) => {
  if (drawType === "Point") {
    return pointStyle;
  } else if (drawType === "LineString") {
    return lineStyle;
  } else if (drawType === "Polygon") {
    return polygonStyle;
  }
  return pointStyle;
};

// Prevents the context menu from opening when right-clicking
export function preventContextMenu(event: MapBrowserEvent<MouseEvent>) {
  event.originalEvent.stopPropagation();
  event.originalEvent.preventDefault();
}

// Sets the tool mode to the feature being drawn
export function handleDrawStart(event: DrawEvent, currentToolMode: ToolMode) {
  const feature = event.feature;
  feature.set("measureType", currentToolMode);
}

type FinishDrawingArgs = {
  event: MapBrowserEvent<PointerEvent>;
  drawInteraction: Draw;
  abortRef: MutableRefObject<boolean>;
  onAbortFeature: () => void;
};

// TODO: Refactor to not use private data (e.g. type_, sketchCoords_, etc.) and remove ts-ignore
export function triggerFinishDrawing({
  event,
  drawInteraction,
  abortRef,
  onAbortFeature,
}: FinishDrawingArgs) {
  // Check if the right mouse button was clicked
  if (event.originalEvent.button !== 2) {
    return;
  }
  // Check the type of the draw interaction
  // @ts-ignore
  if (drawInteraction.type_ === "Point") {
    // If it's a point, do not create a feature and abort the drawing
    abortRef.current = true;
    drawInteraction.finishDrawing();
    return;
  }

  // Check if there are enough points for the given geometry type
  const minPoints = {
    LineString: 2 + 1,
    Polygon: 3 + 1,
  };

  const coordinates =
    // @ts-ignore
    drawInteraction.type_ === "Polygon"
      ? // @ts-ignore
        drawInteraction.sketchLineCoords_
      : // @ts-ignore
        drawInteraction.sketchCoords_;

  if (!coordinates) {
    // first point is not yet set
    drawInteraction.finishDrawing();
    drawInteraction.abortDrawing();

    onAbortFeature();
    return;
  }

  // @ts-ignore
  if (coordinates.length < minPoints[drawInteraction.type_]) {
    abortRef.current = true;
    drawInteraction.abortDrawing();
    drawInteraction.finishDrawing();

    // @ts-ignore
    if (coordinates.length <= minPoints[drawInteraction.type_] - 1) {
      // feature is not complete, call onAbortFeature
      onAbortFeature();
    }
    return;
  }

  // Finish the drawing
  drawInteraction.finishDrawing();
}

export const getMeasureFromSerialized = (
  serializedMeasure: SerializedMeasure,
) => {
  if (serializedMeasure.type === "distance") {
    return LineDistanceMeasure2D.fromSerialized(serializedMeasure);
  } else if (
    serializedMeasure.type === "area" ||
    serializedMeasure.type === "SOLAR_PANEL"
  ) {
    return PolygonArea2D.fromSerialized(serializedMeasure);
  } else if (serializedMeasure.type === "point") {
    return Point2D.fromSerialized(serializedMeasure);
  }

  return null;
};

export const createFeatureForMeasure = (
  measure: Measure,
  featureType: "SOLAR_PANEL" | "record" | "measure",
) => {
  const feature = new Feature({
    name: measure.name,
    geometry: measure.getGeometry(),
  });
  feature.set("measureType", measure.type);
  feature.set("toBeStored", measure.toBeStored);
  feature.set("dbId", measure.dbId);
  feature.setId(measure.id);
  feature.set("name", measure.name);

  switch (featureType) {
    case "record":
      feature.set("isRecord", true);
      break;
    case "SOLAR_PANEL":
      feature.set("isSolarPanel", true);
      break;
    case "measure":
    default:
      feature.set("isMeasure", true);
      break;
  }
  return feature;
};
